import type { LinkProps } from 'next/link';

/**
 * 걸어온 길(브레드크럼). 화면에 들어오면 깊이가 같거나 깊은 칸을 지우고 붙인다.
 * - 목록 버튼 = 부모 칸(지금 화면 바로 왼쪽, history back 아님) / 좌측 메뉴 = 맨 왼쪽 메뉴 칸
 * - 길이 없으면 소유 기준 — 내 것이면 내 퀴즈, 아니면 자료실
 */

export type NavScreenKind =
  | 'public'
  | 'my_quiz'
  | 'classrooms'
  | 'my_board'
  | 'featured'
  | 'my_folders'
  | 'profile'
  | 'profile_folders'
  | 'folder'
  | 'quiz';

/** 목록 버튼이 데려가는 화면 — 상세는 부모 칸이 될 수 없다. */
export type NavParentKind = Exclude<NavScreenKind, 'quiz'>;

type NavScreen = {
  kind: NavScreenKind;
  depth: number;
  /** 길에 메뉴 칸이 없을 때의 고정 좌측 메뉴. 없으면 화면이 알린 소유 기준을 쓴다. */
  fallbackMenu?: string;
};

/** 소유 기준 목록. 좌측 메뉴 href 와 같아야 한다. */
export const OWNERSHIP_LIST_HREF = {
  mine: '/myquiz',
  explore: '/public',
} as const;

export const getOwnershipListHref = (isMine: boolean) =>
  isMine ? OWNERSHIP_LIST_HREF.mine : OWNERSHIP_LIST_HREF.explore;

/** 깊이 표(메뉴 0 → … → 퀴즈 상세 5). 키는 `router.pathname`. 표에 없는 화면으로 가면 길을 비운다. */
export const NAV_SCREENS: Record<string, NavScreen> = {
  // 0 — 좌측 메뉴 화면
  '/public': { kind: 'public', depth: 0 },
  '/myquiz': { kind: 'my_quiz', depth: 0 },
  '/classrooms': { kind: 'classrooms', depth: 0 },
  '/my-board': { kind: 'my_board', depth: 0 },
  // 1 — 메뉴 아래 목록
  '/quiz/featured': {
    kind: 'featured',
    depth: 1,
    fallbackMenu: OWNERSHIP_LIST_HREF.explore,
  },
  '/myquiz/folders': {
    kind: 'my_folders',
    depth: 1,
    fallbackMenu: OWNERSHIP_LIST_HREF.mine,
  },
  // 2·3 — 프로필과 그 아래 목록
  '/profile/[userHashId]': { kind: 'profile', depth: 2 },
  '/profile/[userHashId]/folders': { kind: 'profile_folders', depth: 3 },
  // 4 — 폴더
  '/folder/[id]': { kind: 'folder', depth: 4 },
  // 5 — 상세
  '/quiz/[id]': { kind: 'quiz', depth: 5 },
  '/quiz/[id]/flashcard': { kind: 'quiz', depth: 5 },
};

export const getNavScreen = (pathname: string): NavScreen | undefined =>
  NAV_SCREENS[pathname];

export type NavCrumb = {
  kind: NavScreenKind;
  depth: number;
  /** 들어온 주소 그대로(쿼리 포함). 돌아갈 때 이 주소로 간다. */
  href: string;
};

export type NavPath = NavCrumb[];

/** 쿼리·해시를 뗀 주소. */
export const toNavLocation = (href: string) => href.split(/[?#]/)[0] ?? href;

/** 화면에 들어간다 — 깊이가 같거나 깊은 칸을 지우고 붙인다. */
export function enterNavScreen(path: NavPath, crumb: NavCrumb): NavPath {
  return [...path.filter(item => item.depth < crumb.depth), crumb];
}

/** 화면 이동 하나를 길에 반영한다. `startsNewPath` 면 이전 길을 버리고 새로 시작한다. */
export function navigateNavPath(
  path: NavPath,
  {
    pathname,
    asPath,
    startsNewPath = false,
  }: { pathname: string; asPath: string; startsNewPath?: boolean },
): NavPath {
  const screen = getNavScreen(pathname);
  if (!screen) return [];
  return enterNavScreen(startsNewPath ? [] : path, {
    kind: screen.kind,
    depth: screen.depth,
    href: asPath,
  });
}

/** 부모 칸 — 지금 화면(맨 오른쪽) 바로 왼쪽. 없으면 `null`(소유 기준). */
export const selectParentCrumb = (path: NavPath): NavCrumb | null =>
  path.length >= 2 ? path.at(-2) ?? null : null;

/** 좌측 메뉴 — 맨 왼쪽 칸이 메뉴 화면(깊이 0)이면 그 메뉴. 없으면 `null`(소유 기준). */
export const selectMenuHref = (path: NavPath): string | null => {
  const first = path[0];
  return first && first.depth === 0 ? toNavLocation(first.href) : null;
};

/** 길에 부모 칸이 없을 때 목록 버튼이 갈 곳. `name` 은 라벨에 넣을 이름. */
export type ParentLinkFallback = {
  kind: NavParentKind;
  href: LinkProps['href'];
  name?: string;
};

/**
 * - `pending`: 길·fallback 이 없고 소유도 모른다 — 아직 고르지 않는다
 * - `ready`: `fromPath` 는 부모 칸을 따랐는지(아니면 fallback·소유 기준)
 */
export type ParentLink =
  | { status: 'pending' }
  | {
      status: 'ready';
      kind: NavParentKind;
      href: LinkProps['href'];
      fromPath: boolean;
      name: string;
    };

/** 목록 버튼이 갈 곳 — 부모 칸 → `fallback` → 소유 기준 순. 소유를 모르면 `pending`. */
export function resolveParentLink({
  path,
  labels,
  isMine,
  fallback,
}: {
  path: NavPath;
  /** 주소(쿼리 없이) → 화면 이름. */
  labels: Record<string, string>;
  isMine: boolean | null;
  fallback?: ParentLinkFallback;
}): ParentLink {
  const crumb = selectParentCrumb(path);
  if (crumb && crumb.kind !== 'quiz') {
    return {
      status: 'ready',
      kind: crumb.kind,
      href: crumb.href,
      fromPath: true,
      name: labels[toNavLocation(crumb.href)] ?? '',
    };
  }
  if (fallback) {
    return {
      status: 'ready',
      kind: fallback.kind,
      href: fallback.href,
      fromPath: false,
      name: fallback.name ?? '',
    };
  }
  if (isMine === null) return { status: 'pending' };
  return {
    status: 'ready',
    kind: isMine ? 'my_quiz' : 'public',
    href: getOwnershipListHref(isMine),
    fromPath: false,
    name: '',
  };
}

/**
 * 메뉴 아래 화면의 좌측 메뉴 — 길의 맨 왼쪽 메뉴, 없으면 `fallbackMenu`.
 * `fallbackMenu` 가 `null`(소유 모름)이면 아무 메뉴도 고르지 않는다(`''`).
 */
export function resolveMenuHref(
  path: NavPath,
  fallbackMenu: string | null,
): string {
  if (!fallbackMenu) return '';
  return selectMenuHref(path) ?? fallbackMenu;
}

/** history 항목마다 길을 따로 둔다 — 뒤로·앞으로 가기로 돌아오면 그 항목의 길을 쓴다. */
export type NavPathState = {
  /** `history.state.key` → 그 항목의 길. */
  paths: Record<string, NavPath>;
  /** 오래된 항목부터 지우려고 넣은 순서를 둔다. */
  order: string[];
  currentKey: string | null;
  /** 지난 문서에서 마지막으로 보여 준 항목. 다시 연 문서에서 이어 쓸 길을 찾는다(`entryKey`). */
  lastShownKey: string | null;
  /** 주소(쿼리 없이) → 화면 이름(프로필 사용자 이름·폴더 이름). */
  labels: Record<string, string>;
};

export const EMPTY_NAV_PATH_STATE: NavPathState = {
  paths: {},
  order: [],
  currentKey: null,
  lastShownKey: null,
  labels: {},
};

const MAX_NAV_ENTRIES = 50;

type NavEntry = {
  key: string;
  pathname: string;
  asPath: string;
  startsNewPath?: boolean;
};

/**
 * 지금 history 항목의 길. 기록한 항목·같은 주소면 그대로, 주소만 바뀌었으면(replace) 그 길에서 다시 계산,
 * 처음 보는 항목이면(push) 직전 항목의 길에 이어 붙인다.
 */
export function resolveNavPath(state: NavPathState, entry: NavEntry): NavPath {
  const stored = state.paths[entry.key];
  if (stored?.at(-1)?.href === entry.asPath) {
    return stored;
  }
  const base =
    stored ??
    (state.currentKey !== null ? state.paths[state.currentKey] : undefined) ??
    [];
  return navigateNavPath(base, entry);
}

/** 이동을 마친 뒤 길을 기록한다. 바뀐 게 없으면 같은 객체를 돌려준다. */
export function commitNavEntry(
  state: NavPathState,
  entry: NavEntry,
): NavPathState {
  const path = resolveNavPath(state, entry);
  if (state.paths[entry.key] === path && state.currentKey === entry.key) {
    return state;
  }
  const order = [...state.order.filter(key => key !== entry.key), entry.key];
  const dropped = order.slice(0, Math.max(0, order.length - MAX_NAV_ENTRIES));
  const paths = { ...state.paths, [entry.key]: path };
  dropped.forEach(key => delete paths[key]);
  return {
    ...state,
    paths,
    order: order.slice(dropped.length),
    currentKey: entry.key,
  };
}

export function setNavLabel(
  state: NavPathState,
  location: string,
  label: string,
): NavPathState {
  if (state.labels[location] === label) return state;
  const { [location]: _previous, ...rest } = state.labels;
  // 오래된 이름부터 지운다.
  const kept = Object.entries(rest).slice(-(MAX_NAV_ENTRIES - 1));
  return {
    ...state,
    labels: { ...Object.fromEntries(kept), [location]: label },
  };
}
